import { CommunityAccessType } from '../enums';
import {
  isPaidMembershipTier,
  type CommunityMembershipTierBillingTypeValue,
} from '../commerce/community-membership-tiers';

export type CirclePublishCheckInput = {
  title: string;
  about?: string | null;
  coverUrl?: string | null;
  coverKey?: string | null;
  logoUrl?: string | null;
  logoKey?: string | null;
  slug?: string | null;
  accessType?: string | null;
  tierCount: number;
  hasPaidTier: boolean;
  membershipRefundPolicyText?: string | null;
  connectReady?: boolean;
};

export type CirclePublishCheckItem = {
  key: string;
  label: string;
  passed: boolean;
  blocking: boolean;
  message?: string;
};

export function evaluateCirclePublishChecklist(input: CirclePublishCheckInput): {
  ready: boolean;
  items: CirclePublishCheckItem[];
} {
  const hasTitle = Boolean(input.title.trim());
  const hasAbout = Boolean(input.about?.trim());
  const hasCover = Boolean(input.coverUrl?.trim() || input.coverKey?.trim());
  const hasLogo = Boolean(input.logoUrl?.trim() || input.logoKey?.trim());
  const hasSlug = Boolean(input.slug?.trim());
  const hasTier = input.tierCount > 0;
  const isPaid =
    input.accessType === CommunityAccessType.PAID ||
    input.hasPaidTier;
  const refundPolicyOk =
    !isPaid || Boolean(input.membershipRefundPolicyText?.trim());
  const connectOk = !isPaid || input.connectReady !== false;

  const items: CirclePublishCheckItem[] = [
    {
      key: 'title',
      label: 'Circle name',
      passed: hasTitle,
      blocking: true,
      message: hasTitle ? undefined : 'Add a Circle name.',
    },
    {
      key: 'about',
      label: 'About section',
      passed: hasAbout,
      blocking: true,
      message: hasAbout ? undefined : 'Add an about section before publishing.',
    },
    {
      key: 'tier',
      label: 'Access tier configured',
      passed: hasTier,
      blocking: true,
      message: hasTier ? undefined : 'Add at least one membership tier.',
    },
    {
      key: 'refund_policy',
      label: 'Refund policy (paid Circles)',
      passed: refundPolicyOk,
      blocking: isPaid,
      message: refundPolicyOk ? undefined : 'Add a refund policy for paid Circles.',
    },
    {
      key: 'connect',
      label: 'Stripe Connect ready (paid Circles)',
      passed: connectOk,
      blocking: isPaid,
      message: connectOk
        ? undefined
        : 'Complete Connect onboarding before publishing a paid Circle.',
    },
    {
      key: 'cover',
      label: 'Cover image',
      passed: hasCover,
      blocking: false,
      message: hasCover ? undefined : 'Upload a cover image for a stronger landing page.',
    },
    {
      key: 'logo',
      label: 'Circle logo',
      passed: hasLogo,
      blocking: false,
      message: hasLogo ? undefined : 'Upload a logo for a stronger landing page.',
    },
    {
      key: 'slug',
      label: 'Public URL slug',
      passed: hasSlug,
      blocking: false,
      message: hasSlug ? undefined : 'Choose a public URL slug before or during publish.',
    },
  ];

  const ready = items.filter((item) => item.blocking).every((item) => item.passed);

  return { ready, items };
}

export function circleHasPaidTier(
  tiers: Array<{ billingType: CommunityMembershipTierBillingTypeValue | string }>,
): boolean {
  return tiers.some((tier) =>
    isPaidMembershipTier({
      billingType: tier.billingType as CommunityMembershipTierBillingTypeValue,
    }),
  );
}
